import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TopRightArrowIcon from '../../../../../assets/Svg/TopRightArrowIcon';
import DownloadForOfflineIcon from '../../../../../assets/Svg/DownloadForOfflineIcon';
import { useAppDispatch } from '../../../../../app/hooks';
import { CONSTANTS } from '../../../../../store/investor/constants';
import { updateCompany } from '../../../../../store/investor/action';
import { getDealFormConfig } from '../../../../../store/investor/apiHelpers';
import { VIEW_CONFIGS } from '../../../../../utils/constants/commonConstants';
import { USER_TYPE } from '../../../../../utils/constants/user';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../../../../utils/useVisibilityConfig';
import { fieldCellDisabled } from '../../../../../utils/commonUtil';
import { createDropdownOptions, getPlatformUserId } from '../../../../../utils/utils';
import { HistoryDrawerState } from '../../../../../types/ownerHistory';
import { HISTORY_DRAWER_TITLES } from '../../../../../common/HistoryDrawer/constants';
import { buildValueLabelMap } from '../../../../../common/HistoryDrawer/historyLabelMap';
import {
    CommentCellRenderer,
    DataTableColumn,
    DataTableColumnType,
    DatePickerCellRenderer,
    MultiSelectDropdownCellRenderer,
    NumberCellRenderer,
    OwnerCellRenderer,
    SingleSelectDropdownCellRenderer,
} from '../../../../../common/_custom';
import {
    BorrowerRequirementsCount,
    CellRendererMap,
    CellRendererProps,
    ModalActionCellData,
} from '../../../../../common/_custom/DataTable/DataTable.types';
import { STYLE_CONSTANTS } from '../../../../../common/_custom/DataTable/DataTable.constants';
import { UpdateSingleSelectDropdownProps } from '../../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { UpdateOwnerProps } from '../../../../../common/_custom/DataTable/components/CellRenderers/OwnerCellRenderer';

import { LEAD_COMMENT_SOURCE } from './constants';

// Lead editing building blocks shared by the Leads table (CompaniesList) and the company-page drawer.

// `accountType` is a param (not a sessionStorage read) so this is usable outside the Leads hook.
export function computeBorrowerRequirementsCount(row: any, accountType: string | undefined): ModalActionCellData {
    const requirements: BorrowerRequirementsCount = row?.borrowerRequirementsCount || {};
    const { pending, total, complete } = requirements;

    let statusLabel: string;
    let isResolved: boolean;

    if (accountType === USER_TYPE.INVESTOR) {
        if (pending > 0) {
            statusLabel = `${pending} Request${pending > 1 ? 's' : ''}`;
            isResolved = false;
        } else if (total === complete && total > 0) {
            statusLabel = STYLE_CONSTANTS.LABELS.COMPLETED;
            isResolved = true;
        } else {
            statusLabel = STYLE_CONSTANTS.LABELS.NONE;
            isResolved = true;
        }

        const colors = isResolved ? STYLE_CONSTANTS.COLORS.RESOLVED : STYLE_CONSTANTS.COLORS.PENDING;

        return {
            statusLabel,
            hoverBgColor: colors.HOVER_BG,
            textColor: colors.TEXT,
            iconFill: colors.ICON_FILL,
            hoverFill: colors.HOVER_FILL,
            hoverTextColor: colors.HOVER_TEXT,
            bgColor: colors.BG,
            icon: TopRightArrowIcon,
        };
    } else {
        if (pending > 0) {
            statusLabel = `Upload Data (${pending})`;
            isResolved = false;
        } else if (total === complete && total > 0) {
            statusLabel = pending !== undefined ? `Upload Data (${pending})` : 'Upload Data (0)';
            isResolved = true;
        } else {
            statusLabel = pending !== undefined ? `Upload Data (${pending})` : 'Upload Data (0)';
            isResolved = true;
        }

        const colors = STYLE_CONSTANTS.COLORS.UPLOAD_DATA;

        return {
            statusLabel,
            hoverBgColor: colors.HOVER_BG,
            textColor: colors.TEXT,
            iconFill: colors.ICON_FILL,
            hoverFill: colors.HOVER_FILL,
            hoverTextColor: colors.HOVER_TEXT,
            bgColor: colors.BG,
            icon: DownloadForOfflineIcon,
        };
    }
}

export function buildLeadModalActionConfig({
    handleDataPendingClick,
    accountType,
}: {
    handleDataPendingClick: (row: any) => void;
    accountType: string | undefined;
}) {
    return {
        borrowerRequirementsCount: {
            icon: TopRightArrowIcon,
            handler: handleDataPendingClick,
            computeData: (row: any) => computeBorrowerRequirementsCount(row, accountType),
        },
    };
}

export const LEADS_VALUE_GETTERS = {
    legalName: (row: any) => row?.orgName,
} as const;

// Brand name, falling back to the legal name, as the Leads table's Company Name column shows.
export const resolveLeadDisplayName = (row: any): string => row?.displayName || row?.orgName || '';

export function useLeadsColumnsConfig({
    investorOrgId,
    accountType,
    primaryEmail,
}: {
    investorOrgId: string | undefined;
    accountType: string | undefined;
    primaryEmail?: string;
}) {
    const [leadsColumnsConfig, setLeadsColumnsConfig] = useState<{
        columns: DataTableColumn[];
        pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    }>({ columns: [] });

    useEffect(() => {
        if (!investorOrgId || !accountType) return;

        const fetchConfig = async () => {
            try {
                await getDealFormConfig(
                    {
                        accountType,
                        investorOrgId,
                        primaryEmail,
                        configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                    },
                    {
                        onSuccess: (configData) => {
                            setLeadsColumnsConfig(configData?.leadsColumns ?? { columns: [] });
                        },
                    },
                );
            } catch (error) {
                console.error('Error fetching leads columns config:', error);
            }
        };

        fetchConfig();
    }, [investorOrgId, accountType, primaryEmail]);

    return { leadsColumnsConfig };
}

export function useLeadDynamicDropdownOptions({ partnerData, owners }: { partnerData: any[] | undefined; owners: any[] | undefined }) {
    const partnerNameDropdownOptions = useMemo(() => {
        if (!partnerData || !Array.isArray(partnerData)) return {};
        const filteredOrgs = partnerData.filter((org) => org?.organizationId && org?.organizationDisplayName);
        const params = filteredOrgs.map((org) => ({
            organizationId: org?.organizationId || '',
            organizationDisplayName: org?.organizationDisplayName || '',
        }));
        return createDropdownOptions(params);
    }, [partnerData]);

    const podUserDropdownOptions = useMemo(() => {
        if (!owners || !Array.isArray(owners)) return {};
        const filteredUsers = owners.filter((user) => user?.userId && user?.name);
        const params = filteredUsers.map((user) => ({
            organizationId: user?.userId || '',
            organizationDisplayName: user?.name || '',
        }));
        return createDropdownOptions(params);
    }, [owners]);

    const getStatusDropdownList = useCallback(
        (column: any, _row?: any) => {
            switch (column?.field) {
                case 'customFields.partnerOrgId':
                    return partnerNameDropdownOptions;
                case 'customFields.podUserId':
                    return podUserDropdownOptions;
                default:
                    return null;
            }
        },
        [partnerNameDropdownOptions, podUserDropdownOptions],
    );

    return { partnerNameDropdownOptions, podUserDropdownOptions, getStatusDropdownList };
}

export function useLeadHistoryDrawer({
    partnerNameDropdownOptions,
    podUserDropdownOptions,
}: {
    partnerNameDropdownOptions: any;
    podUserDropdownOptions: any;
}) {
    const [historyDrawerState, setHistoryDrawerState] = useState<HistoryDrawerState>({
        open: false,
        referenceId: '',
        fieldKey: '',
        title: '',
    });

    const handleOwnerHistoryClick = useCallback(
        (row: any, columnField: string, columnLabel?: string, column?: any) => {
            const referenceId = row?.assesseeOrgId;
            if (!referenceId) return;
            const fieldKey = columnField?.split('.')?.[1] || columnField || '';
            const title = columnLabel ? `${columnLabel} History` : HISTORY_DRAWER_TITLES.OWNER_HISTORY;
            const dynamic =
                columnField === 'customFields.partnerOrgId'
                    ? partnerNameDropdownOptions
                    : columnField === 'customFields.podUserId'
                    ? podUserDropdownOptions
                    : undefined;
            const valueLabelMap = buildValueLabelMap(dynamic, column?.dropdownOptions);
            setHistoryDrawerState({ open: true, referenceId, fieldKey, title, companyName: row?.orgName, valueLabelMap });
        },
        [partnerNameDropdownOptions, podUserDropdownOptions],
    );

    const handleHistoryDrawerClose = useCallback(() => {
        setHistoryDrawerState((prev) => ({ ...prev, open: false }));
    }, []);

    return { historyDrawerState, handleOwnerHistoryClick, handleHistoryDrawerClose };
}

export function useLeadFieldCellDisabled() {
    const { leadsFieldVisibility } = useFeatureVisibility({
        leadsFieldVisibility: VISIBILITY_KEYS.DATA_TABLE.LEADS_FIELD_UPDATE,
    });

    return useCallback(
        (columnDisable: boolean | undefined) => fieldCellDisabled(columnDisable, leadsFieldVisibility),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [leadsFieldVisibility.write, leadsFieldVisibility.disabled],
    );
}

type Owner = { userId: string; customSortName?: string; name?: string };

// Save paths shared by the Leads table and the company-page drawer.
export function useLeadFieldUpdaters({ owners, userName }: { owners: Owner[]; userName: string }) {
    const dispatch = useAppDispatch();

    const updateOwner = useCallback(
        ({ row, label, value, ownerType }: UpdateOwnerProps) => {
            const selectedOwner = owners.find((owner) => owner.userId === value);
            const ownerName = selectedOwner?.customSortName || selectedOwner?.name || label || '';

            dispatch(
                updateCompany({
                    orgId: row?.['assesseeOrgId'],
                    owner: {
                        name: ownerName,
                        userId: value || '',
                        activityBy: userName,
                        activityDate: new Date().toISOString(),
                        ownerType: ownerType?.split('.')[1],
                    },
                }),
            );
        },
        [owners, userName, dispatch],
    );

    const updateSingleSelectDropdown = useCallback(
        ({ row, option, column, handlers }: UpdateSingleSelectDropdownProps) => {
            const field = column.field;
            const value = option?.value;
            const orgId = row?.assesseeOrgId;
            if (field === 'companyStatus') {
                // Make API call without customFields
                dispatch(
                    updateCompany(
                        {
                            orgId,
                            companyStatus: {
                                status: value,
                                userId: getPlatformUserId(),
                                activityDate: new Date().getTime(),
                            },
                        },
                        {
                            onSuccess: () => {
                                dispatch({
                                    type: CONSTANTS.UPDATE_COMPANY_STATUS,
                                    payload: {
                                        orgId,
                                        customFields: {
                                            lastStatusUpdateTime: Date.now(),
                                        },
                                    },
                                });
                            },
                        },
                    ),
                );
                return;
            }
            if (field.includes('customFields')) {
                const customFieldKey = field.split('.')[1];
                // Start with the selected custom field value
                const customFields = {
                    [customFieldKey]: value,
                };
                // Merge in any config-driven onChangeUpdateFields, but never overwrite the user's selection
                if (column.onChangeUpdateFields) {
                    Object.entries(column.onChangeUpdateFields).forEach(([k, v]) => {
                        if (k.startsWith('customFields.')) {
                            const fieldKey = k.split('.')[1];
                            if (fieldKey !== customFieldKey) {
                                customFields[fieldKey] = v;
                            }
                        }
                    });
                }
                dispatch(
                    updateCompany(
                        {
                            orgId,
                            customFields,
                        },
                        handlers,
                    ),
                );
                return;
            }
            dispatch(
                updateCompany({
                    orgId,
                    [field]: value,
                }),
            );
        },
        [dispatch],
    );

    return useMemo(() => ({ updateOwner, updateSingleSelectDropdown }), [updateOwner, updateSingleSelectDropdown]);
}

const LEAD_COMMENT_DYNAMIC_PROPS = {
    source: LEAD_COMMENT_SOURCE,
    getOrgId: (row: any) => row?.assesseeOrgId,
    fillCell: true,
    fieldVariant: true,
};

// Module-level so the drawer's COMMENT cell keeps one component identity (an inline arrow would remount it every render).
const LeadFieldCommentCellRenderer: React.FC<CellRendererProps> = (props) => (
    <CommentCellRenderer {...props} dynamicProps={LEAD_COMMENT_DYNAMIC_PROPS} />
);

type Updaters = {
    updateOwner: (...args: any[]) => void;
    updateSingleSelectDropdown: (...args: any[]) => void;
};

// Editable renderers shared by the Leads table and both drawer hosts.
export function useLeadEditableCellRenderers({
    isPartner,
    getLeadsFieldCellDisabled,
    updaters,
    getStatusDropdownList,
    portalDropdowns = false,
    fieldVariant = false,
}: {
    isPartner: boolean;
    getLeadsFieldCellDisabled: (columnDisable: boolean | undefined) => boolean;
    updaters: Updaters;
    getStatusDropdownList: (column: any, row: any) => any;
    // Drawer copies portal dropdown menus so the drawer's scrolling body doesn't clip them.
    portalDropdowns?: boolean;
    // Drawer copies render values as bordered form fields; the table copy leaves this off.
    fieldVariant?: boolean;
}): CellRendererMap {
    return useMemo(
        () => ({
            [DataTableColumnType.OWNER]: (props) => (
                <OwnerCellRenderer
                    {...props}
                    dynamicProps={{
                        updateOwner: updaters.updateOwner,
                        isPartner,
                        isDisabled: getLeadsFieldCellDisabled(props?.column?.disable),
                        ...(fieldVariant ? { fieldVariant: true } : {}),
                        ...(portalDropdowns ? { isConfigTableModalDropDown: true } : {}),
                    }}
                />
            ),
            [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props) => (
                <SingleSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{
                        updateSingleSelectDropdown: updaters.updateSingleSelectDropdown,
                        isPartner,
                        getStatusDropdownList,
                        isDisabled: getLeadsFieldCellDisabled(props?.column?.disable),
                        ...(fieldVariant ? { fieldVariant: true } : {}),
                        ...(portalDropdowns ? { isConfigTableModalDropDown: true } : {}),
                    }}
                />
            ),
            [DataTableColumnType.DATE_PICKER]: (props) => (
                <DatePickerCellRenderer
                    {...props}
                    dynamicProps={{
                        updateCustomFieldStatus: (row, option, column, handlers) => {
                            updaters.updateSingleSelectDropdown({ row, option, column, handlers });
                        },
                        isDisabled: getLeadsFieldCellDisabled(props?.column?.disable),
                        ...(fieldVariant ? { fieldVariant: true } : {}),
                    }}
                />
            ),
            [DataTableColumnType.NUMBER]: (props) => (
                <NumberCellRenderer
                    {...props}
                    dynamicProps={{
                        updateCustomFieldStatus: (row, option, column, handlers) => {
                            updaters.updateSingleSelectDropdown({ row, option, column, handlers });
                        },
                        isDisabled: getLeadsFieldCellDisabled(props?.column?.disable),
                        ...(fieldVariant ? { fieldVariant: true } : {}),
                    }}
                />
            ),
            [DataTableColumnType.MULTI_SELECT_DROPDOWN]: (props) => (
                <MultiSelectDropdownCellRenderer
                    {...props}
                    dynamicProps={{
                        updateMultiSelectDropdown: ({ row, column, selectedValues }) => {
                            updaters.updateSingleSelectDropdown({ row, option: { value: selectedValues }, column, handlers: {} });
                        },
                        isDisabled: getLeadsFieldCellDisabled(props?.column?.disable),
                        ...(fieldVariant ? { fieldVariant: true } : {}),
                        ...(portalDropdowns ? { isConfigTableModalDropDown: true } : {}),
                    }}
                />
            ),
            ...(fieldVariant ? { [DataTableColumnType.COMMENT]: LeadFieldCommentCellRenderer } : {}),
        }),
        [isPartner, getLeadsFieldCellDisabled, updaters, getStatusDropdownList, portalDropdowns, fieldVariant],
    );
}

// Drawer copy of the editable renderers: field styling, and dropdown menus portalled out of the drawer's scrolling body.
export function useLeadDrawerCellRenderers(
    args: Omit<Parameters<typeof useLeadEditableCellRenderers>[0], 'portalDropdowns' | 'fieldVariant'>,
) {
    return useLeadEditableCellRenderers({ ...args, portalDropdowns: true, fieldVariant: true });
}
